// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Crm.tsx
//  CRM — Relacionamento com Clientes
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Users, MessageSquare, CheckCircle2, Clock,
  Eye, ChevronLeft, ChevronRight, AlertCircle,
} from 'lucide-react';
import { useCrm } from '@/features/operacional/hooks/useCrm';
import type { OrdemAtendimentos } from '@/features/operacional/services/crmService';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import type { AtendimentoResumo } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PRIORIDADE_CONFIG: Record<number, { label: string; className: string }> = {
  1: { label: 'Normal',  className: 'bg-blue-500/10 text-blue-500' },
  2: { label: 'Alta',    className: 'bg-orange-500/10 text-orange-500' },
  3: { label: 'Urgente', className: 'bg-red-500/10 text-red-500' },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

const Crm = () => {
  const navigate = useNavigate();

  const [searchTerm,    setSearchTerm]    = useState('');
  const [filtroStatus,  setFiltroStatus]  = useState<string>('todos');
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias pela data de solicitação, mais recentes primeiro.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<OrdemAtendimentos>('solicitacao', true, () => setPagina(1));

  // Busca vai ao servidor: espera a digitação parar.
  const [buscaEnviada, setBuscaEnviada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaEnviada(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { atendimentos, resumo, total, totalPaginas, isLoading, isFetching, deleteAtendimento } =
    useCrm({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada,
        situacao: filtroStatus,
        ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor,
        decrescente: ordenacao.decrescente,
      },
    });

  // Busca e situação já vêm filtradas do servidor; KPIs são do período/busca inteiros.
  const filtrados = atendimentos;
  const totalPendentes  = resumo?.pendentes  ?? 0;
  const totalConcluidos = resumo?.concluidos ?? 0;
  const urgentes        = resumo?.urgentes   ?? 0;
  const totalPeriodo    = resumo?.total      ?? 0;

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CRM — Relacionamento com Clientes</h1>
          <p className="text-muted-foreground mt-1">Registre e acompanhe os contatos com clientes</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/crm/novo')}>
          <Plus className="h-4 w-4" />Novo Atendimento
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalPendentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Sem resultado esperado</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Concluídos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalConcluidos}</div>
            <p className="text-xs text-muted-foreground mt-1">Resultado obtido</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />Urgentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{urgentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Prioridade urgente</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{totalPeriodo}</div>
            <p className="text-xs text-muted-foreground mt-1">atendimentos no período</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <CardTitle>Listagem de Atendimentos</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Número, assunto ou cliente..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Solicitação" />
              <Select value={filtroStatus} onValueChange={(v) => { setFiltroStatus(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                  <TableHead>Cliente</TableHead>
                  <CabecalhoOrdenavel coluna="assunto" ordenacao={ordenacao} decrescentePadrao={false}>Assunto</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="prioridade" ordenacao={ordenacao}>Prioridade</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="solicitacao" ordenacao={ordenacao}>Solicitação</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : filtrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhum atendimento encontrado neste período — amplie o período (ex.: Todo o período) para ver os anteriores'
                        : 'Nenhum atendimento encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtrados.map((a: AtendimentoResumo) => {
                    const prio = PRIORIDADE_CONFIG[a.prioridadeId] ?? PRIORIDADE_CONFIG[1];
                    return (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">#{a.id}</TableCell>
                        <TableCell className="text-muted-foreground">{a.clienteNome ?? `#${a.clienteId}`}</TableCell>
                        <TableCell>{a.sAssunto ?? '—'}</TableCell>
                        <TableCell>
                          <Badge className={prio.className}>{prio.label}</Badge>
                        </TableCell>
                        <TableCell>{new Date(a.dtSolicitacao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          <Badge className={a.isResultadoEsperado
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-blue-500/10 text-blue-500'}>
                            <span className="flex items-center gap-1">
                              {a.isResultadoEsperado
                                ? <><CheckCircle2 className="h-3 w-3" />Concluído</>
                                : <><Clock className="h-3 w-3" />Pendente</>}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost"
                              onClick={() => navigate(`/operacional/crm/${a.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost"
                              onClick={() => setDeletingId(a.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>
                      {page}
                    </Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteAtendimento(deletingId); setDeletingId(null); } }}
        title="Excluir Atendimento"
        description="Tem certeza que deseja excluir este atendimento e todo o seu histórico de contatos?"
      />
    </div>
  );
};

export default Crm;
